// js/auth.js
import createKindeClient from "@kinde-oss/kinde-auth-pkce-js";

const kindeDomain = process.env.KINDE_DOMAIN;
const kindeClientId = process.env.KINDE_CLIENT_ID;

// The single URL where Kinde will always redirect back after login/registration
// This MUST be listed in your Kinde 'Allowed callback URLs'
const kindeRedirectUri = window.location.origin;
// The single URL where Kinde will redirect back after logout
// This MUST be listed in your Kinde 'Allowed logout URLs'
const kindeLogoutUri = window.location.origin;


let kindeInstance = null; // Hold the instance globally within this module

// Function to setup the logout button listener
const setupLogoutButton = () => {
    const logoutButton = document.getElementById('logout');
    if (logoutButton && kindeInstance) {
        console.log("Setting up logout button listener.");
        const newLogoutButton = logoutButton.cloneNode(true);
        logoutButton.parentNode.replaceChild(newLogoutButton, logoutButton);
        document.getElementById('logout').addEventListener('click', async (e) => {
            e.preventDefault();
            if (kindeInstance) {
                try {
                    console.log("Logout button clicked. Initiating Kinde logout...");
                    await kindeInstance.logout();
                    console.log("Kinde logout initiated, redirecting...");
                } catch (logoutError) {
                    console.error("Error during kinde.logout():", logoutError);
                }
            } else {
                console.error("Logout button clicked, but Kinde instance is not available.");
            }
        });
    } else if (!logoutButton) {
        console.warn('Logout button with id "logout" not found.');
    } else {
        console.warn('Logout button found, but Kinde instance is null.');
    }
};

// Main function to initialize Kinde and check authentication
const initializeKinde = async () => {
    console.log("Initializing Kinde...");
    if (!kindeDomain || !kindeClientId) {
        console.error("Kinde environment variables (KINDE_DOMAIN, KINDE_CLIENT_ID) are not set.");
        document.body.innerHTML = '<h1>Configuration Error</h1><p>Kinde authentication cannot be initialized.</p>';
        return false;
    }

    try {
        kindeInstance = await createKindeClient({
            client_id: kindeClientId,
            domain: kindeDomain,
            redirect_uri: kindeRedirectUri, // Use the constant redirect URI
            logout_uri: kindeLogoutUri,   // Use the constant logout URI
            // is_dangerously_use_local_storage: true
            // is_dangerously_use_local_storage: process.env.NODE_ENV !== 'production',
        });
        console.log("Kinde client created.");

        const params = new URLSearchParams(window.location.search);

        // Scenario 1: Handle redirect back from Kinde LOGIN
        if (params.has("code") && params.has("state")) {
            console.log("Detected Kinde login redirect callback parameters. Handling...");
            // Process the callback and get user info AND appState
            const {appState} = await kindeInstance.handleRedirectCallback(); // Destructure to get appState
            console.log("Kinde redirect callback handled. AppState:", appState);

            // Clean the URL
            window.history.replaceState({}, document.title, window.location.pathname);
            console.log("URL cleaned after login redirect.");

            // **** ADDED: Redirect to original intended page ****
            if (appState?.intendedRoute) {
                console.log("Redirecting to intended route:", appState.intendedRoute);
                window.location.href = appState.intendedRoute;
                // Stop further execution on this page as we are redirecting
                // Use a Promise that never resolves to halt script execution here
                return new Promise(() => {});
            } else {
                 console.log("No intended route found in appState, staying on redirect URI.");
                 // User is authenticated, setup UI on the current (root) page.
                 setupLogoutButton();
                 return true; // Indicate success on the current page
             }
            // ***************************************************

        } else {
            // Scenario 2: Not a login redirect. Check if user is ALREADY authenticated.
            console.log("No Kinde login redirect detected. Checking current authentication status...");
            const isAuthenticated = await kindeInstance.isAuthenticated();
            console.log("Is Authenticated:", isAuthenticated);

            if (isAuthenticated) {
                // User has an active session. Setup logout.
                console.log("User is authenticated.");
                setupLogoutButton();
                return true; // Indicate success, allow page load
            } else {
                // Scenario 3: User is not authenticated. Redirect to Kinde login.
                // **** ADDED: Pass current URL as app_state ****
                console.log("User is not authenticated. Redirecting to Kinde login page...");
                await kindeInstance.login({
                     app_state: { intendedRoute: window.location.href } // Store the current page URL
                 });
                 // *********************************************
                return false; // Indicate redirection
            }
        }

    } catch (error) {
        console.error("Error during Kinde initialization or authentication check:", error);
        document.body.innerHTML = `<h1>Authentication Error</h1><p>An error occurred: ${error.message || error}. Please try again later.</p>`;
        return false;
    }
};

// --- Start the authentication flow ---
initializeKinde().then(authSuccessful => {
    if (authSuccessful === true) { // Check explicitly for true, as false/Promise mean redirection/pending
        console.log("Authentication successful. Main application logic can proceed.");
    } else {
        console.log("Authentication pending (redirecting) or failed. Application logic paused.");
    }
}).catch(err => {
    console.error("Unhandled error during Kinde initialization promise chain:", err);
    document.body.innerHTML = '<h1>Unexpected Error</h1><p>An unexpected error occurred during authentication setup.</p>';
});